import type { ApiConfig, PaginatedResponse, AuthTokens, AppUser, UploadResult } from './types';

export class ApiClientError extends Error {
  constructor(
    message: string,
    public statusCode: number,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export class AppApiClient {
  private config: ApiConfig;
  private authToken: string | null = null;

  constructor(config: ApiConfig) {
    this.config = config;
  }

  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  // --- CRUD ---

  async listRecords(
    entity: string,
    options?: { limit?: number; cursor?: string; since?: string },
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    const limit = options?.limit ?? 50;
    let url = `${this.baseUrl}/${entity}?limit=${limit}`;
    if (options?.cursor) url += `&cursor=${options.cursor}`;
    if (options?.since) url += `&since=${encodeURIComponent(options.since)}`;
    return this.request('GET', url);
  }

  async getRecord(entity: string, recordId: string): Promise<Record<string, unknown>> {
    return this.request('GET', `${this.baseUrl}/${entity}/${recordId}`);
  }

  async createRecord(entity: string, data: Record<string, unknown>, meta?: { clientUpdatedAt?: string; deviceId?: string }): Promise<Record<string, unknown>> {
    const body: Record<string, unknown> = { ...data };
    if (meta?.clientUpdatedAt !== undefined) body.clientUpdatedAt = meta.clientUpdatedAt;
    if (meta?.deviceId !== undefined) body.deviceId = meta.deviceId;
    return this.request('POST', `${this.baseUrl}/${entity}`, body);
  }

  async updateRecord(entity: string, recordId: string, data: Record<string, unknown>, meta?: { clientUpdatedAt?: string; deviceId?: string }): Promise<Record<string, unknown>> {
    const body: Record<string, unknown> = { ...data };
    if (meta?.clientUpdatedAt !== undefined) body.clientUpdatedAt = meta.clientUpdatedAt;
    if (meta?.deviceId !== undefined) body.deviceId = meta.deviceId;
    return this.request('PUT', `${this.baseUrl}/${entity}/${recordId}`, body);
  }

  async deleteRecord(entity: string, recordId: string, meta?: { clientUpdatedAt?: string; deviceId?: string }): Promise<{ deleted: boolean; id: string }> {
    const body: Record<string, unknown> | undefined = (meta?.clientUpdatedAt !== undefined || meta?.deviceId !== undefined)
      ? { ...(meta?.clientUpdatedAt !== undefined ? { clientUpdatedAt: meta.clientUpdatedAt } : {}), ...(meta?.deviceId !== undefined ? { deviceId: meta.deviceId } : {}) }
      : undefined;
    return this.request('DELETE', `${this.baseUrl}/${entity}/${recordId}`, body);
  }

  // --- Auth ---

  async signUp(email: string, password: string): Promise<{ userSub: string; confirmed: boolean }> {
    return this.request('POST', `${this.baseUrl}/auth/signup`, { email, password });
  }

  async signIn(email: string, password: string): Promise<AuthTokens> {
    const result = await this.request<AuthTokens>('POST', `${this.baseUrl}/auth/signin`, { email, password });
    this.authToken = result.accessToken;
    return result;
  }

  async signOut(): Promise<void> {
    await this.request('POST', `${this.baseUrl}/auth/signout`);
    this.authToken = null;
  }

  async getMe(): Promise<AppUser> {
    return this.request('GET', `${this.baseUrl}/auth/me`);
  }

  // --- Upload ---

  async getUploadUrl(
    fileName: string,
    contentType: string,
    entityName?: string,
    recordId?: string,
  ): Promise<UploadResult> {
    return this.request('POST', `${this.baseUrl}/upload`, {
      fileName,
      contentType,
      entityName,
      recordId,
    });
  }

  async uploadFile(file: Blob | ArrayBuffer, fileName: string, contentType: string, entityName?: string, recordId?: string): Promise<string> {
    const { uploadUrl, key } = await this.getUploadUrl(fileName, contentType, entityName, recordId);

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });

    if (!response.ok) throw new ApiClientError('Upload failed', response.status);

    return key;
  }

  // --- Internal ---

  private get baseUrl(): string {
    return `${this.config.baseUrl}/apps/${this.config.appId}`;
  }

  private async request<T = any>(method: string, url: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiClientError(
        data?.error ?? `Request failed with status ${response.status}`,
        response.status,
      );
    }

    return data as T;
  }
}
